import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { SidResponseVerify } from '@sdir/httpclient/lib/clients/aps/personalcertificate';
import { FormInput } from '@sdir/blueprint.aps/lib/components';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import { Loader, SdirButton } from '@sdir/sds';
import { sidVerifyApi } from '../../../httpclient';
import Sid from '../../Organisms/Sid';
import { useFeatureToggles } from '../../../services/hooks/useFeatureToggles';
import { ToggleDisableSid } from '../../../constants/permissions';

const SearchSidPage: React.FC = () => {
  const intl = useIntl();
  const featureToggle = useFeatureToggles();
  const [searchError, setSearchError] = useState<any>(null);
  const [sid, setSid] = useState<SidResponseVerify>();
  const [loading, setLoading] = useState<boolean>(false);
  const enabledSid = !isToggleOn(featureToggle?.result, ToggleDisableSid, '');

  const initialValues = {
    documentNumber: '',
  };

  const handleSubmit = useCallback(
    (values) => {
      setLoading(true);
      sidVerifyApi
        .v1SeafareridentitydocumentGetbyidverifyDocumentNumberGet(values?.documentNumber)
        .then((response) => {
          if (response.status === 200) {
            setSid(response.data);
            setSearchError(undefined);
          }
          setLoading(false);
        })
        .catch((error) => {
          const { status } = error?.response || {};
          if (status >= 400 && status < 500) {
            setSearchError(intl.formatMessage({ id: 'sid.notfound' }));
          } else {
            setSearchError(error);
          }
          setLoading(false);
        });
    },
    [intl]
  );

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      documentNumber: Yup.string().required(intl.formatMessage({ id: 'required.field' })),
    });
  }, [intl]);

  if (!enabledSid) return <></>;

  return (
    <>
      <h1>{intl.formatMessage({ id: 'sid.search.header' })}</h1>
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {() => (
          <StyledForm>
            <Field
              label={intl.formatMessage({ id: 'sid.number' })}
              name="documentNumber"
              component={FormInput}
            />
            <StyledSdirButton
              htmlType="submit"
              type="primary"
              text={intl.formatMessage({ id: 'search' })}
            />
            {loading && <Loader />}
            {searchError && <ErrorMessage error={searchError} />}
          </StyledForm>
        )}
      </Formik>
      {sid && !searchError && (
        <ResultWrapper>
          <Sid sid={sid} />
        </ResultWrapper>
      )}
    </>
  );
};

const StyledForm = styled(Form)`
  display: flex;
  grid-row-gap: 1em;
  flex-direction: column;
  max-width: 400px;
`;

const StyledSdirButton = styled(SdirButton)`
  width: fit-content;
`;

const ResultWrapper = styled.div`
  margin: 20px 0;

  p {
    margin: 0;
  }
`;

export default SearchSidPage;
